import { useDispatch } from "react-redux";
import { UnitTable } from "./components/units/UnitTable";
import { UnitTableControls } from "./components/controls/UnitTableControls";
import { useEffect, useState, useMemo } from "react";
import { setApiKey } from "./features/api/apiKeySlice";
import {
    DisplayOptionContext,
    defaultDisplayOption,
} from "@/context/DisplayOptionContext";
import { validateDisplayOptions } from "./lib/validations";
import type { SeVaporAppProps, UnitGroup } from "./types";
import { useGetUnitGroupsQuery } from "./features/api/apiSlice";
import merge from "lodash/merge";

const App = ({ apiKey, options }: SeVaporAppProps) => {
    const dispatch = useDispatch();

    const [selectedFeatures, setSelectedFeatures] = useState<number[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const [selectedSort, setSelectedSort] = useState<string>("");

    useEffect(() => {
        dispatch(setApiKey(apiKey));
    }, [apiKey, dispatch]);

    const mergedOptions = merge({}, defaultDisplayOption, options);

    try {
        validateDisplayOptions(mergedOptions);
    } catch (error) {
        if (error instanceof Error) {
            console.error(error.message); // Safe to access `message`
        } else {
            console.error("An unknown error occurred.");
        }
    }

    const { facilityIds } = mergedOptions;

    const unitParams = {
        "facility-ids": facilityIds.join(","),
        ...(mergedOptions.additionalUnitSourceInfo
            ? { "unit-source": mergedOptions.additionalUnitSourceInfo }
            : {}),
        ...(mergedOptions.allowedFeatures
            ? { features: mergedOptions.allowedFeatures }
            : {}),
        ...(mergedOptions.allowedCategories
            ? { categories: mergedOptions.allowedCategories }
            : {}),
        ...(mergedOptions.sort ? { sort: mergedOptions.sort } : {}),
        ...(mergedOptions.groupBy ? { group: mergedOptions.groupBy } : {}),
    };

    const {
        data: unitGroupsData,
        error: unitGroupsError,
        isLoading: unitGroupsLoading,
    } = useGetUnitGroupsQuery(unitParams, { skip: !apiKey });

    // Extract unique features from unitGroupsData
    const availableFeatures = useMemo(() => {
        return Array.from(
            new Map(
                unitGroupsData?.data
                    ?.flatMap(
                        (unitGroup: UnitGroup) =>
                            unitGroup.representative_unit.features,
                    )
                    .map((feature) => [feature.id, feature]) ?? [],
            ).values(),
        );
    }, [unitGroupsData]);
    const availableCategories = useMemo(() => {
        return Array.from(
            new Map(
                unitGroupsData?.data
                    ?.filter(
                        (unitGroup: UnitGroup) =>
                            unitGroup.representative_unit.category !== null,
                    )
                    .map((unitGroup: UnitGroup) => [
                        unitGroup.representative_unit.category.id,
                        unitGroup.representative_unit.category,
                    ]) ?? [],
            ).values(),
        );
    }, [unitGroupsData]);
    return (
        <DisplayOptionContext.Provider value={mergedOptions}>
            <UnitTableControls
                features={availableFeatures}
                selectedFeatures={selectedFeatures}
                setSelectedFeatures={setSelectedFeatures}
                categories={availableCategories}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                selectedSort={selectedSort}
                setSelectedSort={setSelectedSort}
            />
            <UnitTable
                facilityIds={facilityIds}
                units={unitGroupsData?.data ?? []}
                unitsError={unitGroupsError}
                unitsIsLoading={unitGroupsLoading}
                selectedFeatures={selectedFeatures}
                selectedCategories={selectedCategories}
                sort={selectedSort}
            />
        </DisplayOptionContext.Provider>
    );
};

export default App;
